import { formatMemoryForDisplay } from "../../utils/index";

interface FixedStorage {
  count?: number;
  size: string;
  type: string;
}

const formatStorageSize = (size: string): string => {
  const match = size.trim().match(/^(\d+(?:\.\d+)?)\s*(TiB|GiB|TB|GB)$/i);

  if (!match) {
    return formatMemoryForDisplay(size);
  }

  const units: Record<string, string> = {
    tib: "TiB",
    gib: "GiB",
    tb: "TB",
    gb: "GB",
  };

  return `${match[1]} ${units[match[2].toLowerCase()]}`;
};

const getStorageTypeLabel = (type: string): string | undefined => {
  const normalizedType = type.toLowerCase();

  if (normalizedType.includes("ssd")) {
    return "SSD";
  }

  if (normalizedType.includes("nvme")) {
    return "NVMe";
  }

  return undefined;
};

export const formatFixedStorage = (
  storageDevices: FixedStorage[],
): string | undefined => {
  const groups = new Map<
    string,
    { count: number; size: string; typeLabel: string }
  >();

  storageDevices.forEach((storage) => {
    const typeLabel = getStorageTypeLabel(storage.type);
    const size = formatStorageSize(storage.size);

    if (!typeLabel || !size) {
      return;
    }

    const key = `${size}:${typeLabel}`;
    const existingGroup = groups.get(key);
    const count =
      storage.count !== undefined &&
      Number.isFinite(storage.count) &&
      storage.count > 0
        ? storage.count
        : 1;

    if (existingGroup) {
      existingGroup.count += count;
    } else {
      groups.set(key, { count, size, typeLabel });
    }
  });

  if (groups.size === 0) {
    return undefined;
  }

  return Array.from(groups.values())
    .map(({ count, size, typeLabel }) =>
      count > 1 ? `${count} × ${size} ${typeLabel}` : `${size} ${typeLabel}`,
    )
    .join(" and ");
};
